import {
  type CreateCruiseCabinRequest,
  type CruiseCabin,
  type CruiseCabinRate,
} from '@utazzo/shared';
import { UzBadge, UzButton, UzConfirmDialog, UzTooltip } from '@utazzo/ui';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  BedDouble,
  ChevronRight,
  Pencil,
  Plus,
  Trash2,
  Users,
  Utensils,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import {
  useCreateCruiseCabin,
  useCreateCruiseRate,
  useDeleteCruiseCabin,
  useDeleteCruiseRate,
  useUpdateCruiseCabin,
  useUpdateCruiseRate,
} from '../use-cruises';

import { CabinFormDialog } from './CabinFormDialog';
import { CabinRateFormPanel } from './CabinRateFormPanel';

/**
 * Cabins & Rates - the cruise analogue of the hotel Rooms & rates tab. Each
 * cabin category is an expandable section with its rate cards nested inside.
 * Adding a rate happens in the context of a cabin (no disconnected dropdown
 * picking), so the data can't end up mis-keyed. Rates without a cabin link
 * (priced generically by room type/meal plan/occupancy) collect in an
 * "Unassigned rates" section at the bottom.
 */

interface CabinsRatesTabProps {
  cruiseId: string;
  cabins: CruiseCabin[];
  rates: CruiseCabinRate[];
  ratesLoading: boolean;
  disabled: boolean;
}

const fmtPrice = (amount: number, currencyCode: string | null): string => {
  if (!Number.isFinite(amount)) return String(amount);
  const code = currencyCode ?? 'INR';
  const prefix = code === 'INR' ? '₹' : `${code} `;
  return `${prefix}${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};

/** Short label for a weekday mask: All days / Weekdays / Weekends / custom. */
const weekdayLabel = (mask: number): string | null => {
  if (mask === 127) return null; // all days - no badge needed
  if (mask === 0b0111110) return 'Weekdays';
  if (mask === 0b1000001) return 'Weekends';
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return [1, 2, 3, 4, 5, 6, 0]
    .filter((b) => (mask & (1 << b)) !== 0)
    .map((b) => names[b])
    .join(', ');
};

/** Headline price for a rate row: per-pax adult rate, falling back to fixed cost. */
const headlinePrice = (rate: CruiseCabinRate): string => {
  if (rate.adultPrice > 0) {
    return `${fmtPrice(rate.adultPrice, rate.currencyCode)} / adult`;
  }
  if (rate.fixedCabinCost > 0) {
    return `${fmtPrice(rate.fixedCabinCost, rate.currencyCode)} / cabin`;
  }
  return 'No price set';
};

// ---------------------------------------------------------------------------
// Rate row - one line inside a cabin section
// ---------------------------------------------------------------------------

interface RateRowProps {
  rate: CruiseCabinRate;
  onEdit: () => void;
  onDelete: () => void;
  disabled: boolean;
}

/** Compact "label price" chip for the breakdown line. Hidden when zero. */
const PriceChip = ({
  label,
  amount,
  currencyCode,
}: {
  label: string;
  amount: number;
  currencyCode: string | null;
}): React.JSX.Element | null => {
  if (!amount) return null;
  return (
    <span className="text-xs whitespace-nowrap text-zinc-500">
      {label}{' '}
      <span className="font-medium text-zinc-700 tabular-nums">
        {fmtPrice(amount, currencyCode)}
      </span>
    </span>
  );
};

const RateRow = ({ rate, onEdit, onDelete, disabled }: RateRowProps): React.JSX.Element => (
  <div className="group px-4 py-2.5 transition-colors hover:bg-zinc-50/60">
    <div className="flex items-center gap-3">
      <span className="flex w-36 shrink-0 flex-col text-xs text-zinc-600">
        <span className="tabular-nums">
          {fmtDate(rate.validityFrom)} - {fmtDate(rate.validityTo)}
        </span>
        {weekdayLabel(rate.weekdayMask) && (
          <span className="text-[11px] text-zinc-400">{weekdayLabel(rate.weekdayMask)}</span>
        )}
      </span>
      <span className="flex w-40 shrink-0 flex-col text-xs text-zinc-600">
        <span className="truncate">{rate.roomTypeName ?? '-'}</span>
        <span className="truncate text-[11px] text-zinc-400">
          {rate.occupancyTypeName ?? '-'} · {rate.mealPlanName ?? '-'}
        </span>
      </span>
      <span
        className={
          'w-32 shrink-0 text-sm font-medium tabular-nums ' +
          (headlinePrice(rate) === 'No price set' ? 'text-amber-600' : 'text-zinc-900')
        }
      >
        {headlinePrice(rate)}
      </span>
      {rate.blackoutDates.length > 0 && (
        <span className="hidden items-center gap-1 text-xs text-red-500 sm:flex">
          <span className="tabular-nums">{rate.blackoutDates.length} blackout</span>
        </span>
      )}
      <span className="flex-1" />
      <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
        <span
          className={
            'h-1.5 w-1.5 rounded-full ' + (rate.isActive ? 'bg-emerald-500' : 'bg-zinc-400')
          }
          aria-hidden
        />
        {rate.isActive ? 'Active' : 'Inactive'}
      </span>
      <div className="flex items-center gap-px">
        <UzTooltip label="Edit rate">
          <UzButton
            variant="ghost"
            size="icon"
            onClick={onEdit}
            disabled={disabled}
            aria-label="Edit rate"
            className="h-7 w-7 text-zinc-400 hover:text-zinc-900"
          >
            <Pencil className="h-3.5 w-3.5" />
          </UzButton>
        </UzTooltip>
        <UzTooltip label="Delete rate">
          <UzButton
            variant="ghost"
            size="icon"
            onClick={onDelete}
            disabled={disabled}
            aria-label="Delete rate"
            className="h-7 w-7 text-zinc-400 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </UzButton>
        </UzTooltip>
      </div>
    </div>
    {/* Full price matrix line - every component of the rate at a glance. */}
    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
      <PriceChip
        label="Extra adult"
        amount={rate.extraAdultPrice}
        currencyCode={rate.currencyCode}
      />
      <PriceChip
        label="Extra child"
        amount={rate.extraChildPrice}
        currencyCode={rate.currencyCode}
      />
      <PriceChip
        label="Child + bed"
        amount={rate.childWithBedPrice}
        currencyCode={rate.currencyCode}
      />
      <PriceChip
        label="Child no bed"
        amount={rate.childWithoutBedPrice}
        currencyCode={rate.currencyCode}
      />
      <PriceChip label="Infant" amount={rate.infantPrice} currencyCode={rate.currencyCode} />
      {rate.adultPrice > 0 && rate.fixedCabinCost > 0 && (
        <PriceChip
          label="Fixed cabin"
          amount={rate.fixedCabinCost}
          currencyCode={rate.currencyCode}
        />
      )}
      {rate.mealOptions.length > 0 && (
        <span className="inline-flex items-center gap-1 text-xs whitespace-nowrap text-zinc-500">
          <Utensils className="h-3 w-3 text-zinc-400" aria-hidden />
          {rate.mealOptions
            .map((m) => m.mealPlanCode ?? m.mealPlanName)
            .filter(Boolean)
            .join(', ')}
        </span>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

export const CabinsRatesTab = ({
  cruiseId,
  cabins,
  rates,
  ratesLoading,
  disabled,
}: CabinsRatesTabProps): React.JSX.Element => {
  // Cabin CRUD
  const createCabin = useCreateCruiseCabin(cruiseId);
  const updateCabin = useUpdateCruiseCabin(cruiseId);
  const deleteCabin = useDeleteCruiseCabin(cruiseId);
  const [cabinDialog, setCabinDialog] = useState<'create' | CruiseCabin | null>(null);
  const [cabinDelete, setCabinDelete] = useState<CruiseCabin | null>(null);

  // Rate CRUD
  const createRate = useCreateCruiseRate(cruiseId);
  const updateRate = useUpdateCruiseRate(cruiseId);
  const deleteRate = useDeleteCruiseRate(cruiseId);
  /** Which cabin has the inline rate form open; 'unassigned' for the bottom section. */
  const [rateFormCabin, setRateFormCabin] = useState<string | null>(null);
  const [rateEditing, setRateEditing] = useState<CruiseCabinRate | null>(null);
  const [rateDelete, setRateDelete] = useState<CruiseCabinRate | null>(null);

  const pending =
    disabled ||
    createCabin.isPending ||
    updateCabin.isPending ||
    deleteCabin.isPending ||
    createRate.isPending ||
    updateRate.isPending ||
    deleteRate.isPending;

  // Group rates by cabin; null cruiseCabinId goes to 'unassigned'.
  const ratesByCabin = useMemo(() => {
    const map = new Map<string, CruiseCabinRate[]>();
    for (const r of rates) {
      const key = r.cruiseCabinId ?? 'unassigned';
      const list = map.get(key) ?? [];
      list.push(r);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.validityFrom.localeCompare(b.validityFrom));
    }
    return map;
  }, [rates]);
  const unassigned = ratesByCabin.get('unassigned') ?? [];

  // Expansion - all expanded by default for <=4 cabins, else first only.
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(cabins.length > 4 ? cabins.slice(1).map((c) => c.id) : []),
  );
  const toggle = (id: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openRateForm = (cabinKey: string, rate: CruiseCabinRate | null): void => {
    setRateFormCabin(cabinKey);
    setRateEditing(rate);
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(cabinKey);
      return next;
    });
  };
  const closeRateForm = (): void => {
    setRateFormCabin(null);
    setRateEditing(null);
  };

  const submitRate = async (
    payload: Parameters<typeof createRate.mutateAsync>[0],
  ): Promise<void> => {
    if (rateEditing) {
      await updateRate.mutateAsync({ rateId: rateEditing.id, payload });
    } else {
      await createRate.mutateAsync(payload);
    }
    closeRateForm();
  };

  const submitCabin = async (values: CreateCruiseCabinRequest): Promise<void> => {
    if (cabinDialog && cabinDialog !== 'create') {
      await updateCabin.mutateAsync({ cabinId: cabinDialog.id, payload: values });
    } else {
      await createCabin.mutateAsync(values);
    }
    setCabinDialog(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Cabins & rates</h3>
          <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">
            Each cabin holds its sailing rate cards - per-pax rates, extra beds and meal upgrades
            all live in the rate. A cabin without prices can't go into an itinerary.
          </p>
        </div>
        <UzButton size="sm" onClick={() => setCabinDialog('create')} disabled={pending}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add cabin
        </UzButton>
      </div>

      {/* Empty state */}
      {cabins.length === 0 && !ratesLoading && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-12 text-center">
          <BedDouble className="h-8 w-8 text-zinc-300" strokeWidth={1.5} aria-hidden />
          <div>
            <h4 className="text-sm font-semibold text-zinc-900">No cabins yet</h4>
            <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-zinc-500">
              Add the cabin categories this cruise offers (Interior, Ocean View, Balcony...), then
              add sailing rates inside each cabin.
            </p>
          </div>
          <UzButton size="sm" onClick={() => setCabinDialog('create')} disabled={pending}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add the first cabin
          </UzButton>
        </div>
      )}

      {/* Cabin sections */}
      <motion.div layout className="space-y-3">
        {cabins.map((cabin) => {
          const cabinRates = ratesByCabin.get(cabin.id) ?? [];
          const isCollapsed = collapsed.has(cabin.id);
          const formOpenHere = rateFormCabin === cabin.id;
          return (
            <section
              key={cabin.id}
              className="overflow-hidden rounded-lg border border-zinc-200 bg-white"
            >
              {/* Cabin header - the expand toggle is its own button so the
                  row actions are real siblings, not nested interactives. */}
              <div className="flex items-center gap-1 px-4 py-3">
                <button
                  type="button"
                  onClick={() => toggle(cabin.id)}
                  aria-expanded={!isCollapsed}
                  aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${cabin.name}`}
                  className="-mx-2 flex min-w-0 flex-1 items-center gap-3 rounded px-2 py-1 text-left transition-colors hover:bg-zinc-50"
                >
                  <ChevronRight
                    className={
                      'h-4 w-4 shrink-0 text-zinc-400 transition-transform duration-150 ' +
                      (isCollapsed ? '' : 'rotate-90')
                    }
                    aria-hidden
                  />
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-sm font-semibold tracking-tight text-zinc-900">
                      {cabin.name}
                    </span>
                    {cabin.deck && <span className="text-xs text-zinc-500">{cabin.deck}</span>}
                    {cabin.roomTypeName && (
                      <span className="text-xs text-zinc-500">{cabin.roomTypeName}</span>
                    )}
                    <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                      <Users className="h-3 w-3 text-zinc-400" aria-hidden />
                      {cabin.minOccupancy === cabin.maxOccupancy
                        ? `${cabin.minOccupancy} pax`
                        : `${cabin.minOccupancy}-${cabin.maxOccupancy} pax`}
                    </span>
                    {!cabin.isActive && <UzBadge tone="secondary">Inactive</UzBadge>}
                    {cabinRates.length === 0 && !ratesLoading && (
                      <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                        <AlertTriangle className="h-3 w-3" aria-hidden />
                        No prices
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-zinc-400 tabular-nums">
                    {cabinRates.length > 0 &&
                      `${cabinRates.length} rate${cabinRates.length === 1 ? '' : 's'}`}
                  </span>
                </button>
                <div className="flex shrink-0 items-center gap-px">
                  <UzButton
                    variant="ghost"
                    size="sm"
                    onClick={() => openRateForm(cabin.id, null)}
                    disabled={pending}
                    className="h-7 text-xs text-zinc-600 hover:text-zinc-900"
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Add rate
                  </UzButton>
                  <UzTooltip label="Edit cabin">
                    <UzButton
                      variant="ghost"
                      size="icon"
                      onClick={() => setCabinDialog(cabin)}
                      disabled={pending}
                      aria-label={`Edit ${cabin.name}`}
                      className="h-7 w-7 text-zinc-400 hover:text-zinc-900"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </UzButton>
                  </UzTooltip>
                  <UzTooltip label="Delete cabin">
                    <UzButton
                      variant="ghost"
                      size="icon"
                      onClick={() => setCabinDelete(cabin)}
                      disabled={pending}
                      aria-label={`Delete ${cabin.name}`}
                      className="h-7 w-7 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </UzButton>
                  </UzTooltip>
                </div>
              </div>

              {/* Rates */}
              {!isCollapsed && (
                <div className="border-t border-zinc-100">
                  {ratesLoading ? (
                    <div className="space-y-2 p-4">
                      <div className="h-8 animate-pulse rounded bg-zinc-50" />
                      <div className="h-8 animate-pulse rounded bg-zinc-50" />
                    </div>
                  ) : cabinRates.length === 0 && !formOpenHere ? (
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      <p className="text-xs text-zinc-500">
                        No rate cards yet. Add one so this cabin can be priced.
                      </p>
                      <UzButton
                        variant="outline"
                        size="sm"
                        onClick={() => openRateForm(cabin.id, null)}
                        disabled={pending}
                        className="h-7 text-xs"
                      >
                        <Plus className="mr-1 h-3 w-3" />
                        Add rate
                      </UzButton>
                    </div>
                  ) : (
                    <div className="divide-y divide-zinc-100">
                      {cabinRates.map((rate) => (
                        <RateRow
                          key={rate.id}
                          rate={rate}
                          onEdit={() => openRateForm(cabin.id, rate)}
                          onDelete={() => setRateDelete(rate)}
                          disabled={pending}
                        />
                      ))}
                    </div>
                  )}

                  {formOpenHere && (
                    <div className="border-t border-zinc-100 bg-zinc-50/40 p-4">
                      <CabinRateFormPanel
                        key={rateEditing?.id ?? `new-${cabin.id}`}
                        cabinScope={{
                          id: cabin.id,
                          name: cabin.name,
                          maxOccupancy: cabin.maxOccupancy,
                        }}
                        cabins={cabins}
                        initial={rateEditing}
                        pending={pending}
                        onSubmit={submitRate}
                        onCancel={closeRateForm}
                      />
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </motion.div>

      {/* Unassigned rates (priced generically by room type/meal/occupancy) */}
      {unassigned.length > 0 && (
        <section className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
          <div className="flex items-center gap-2 border-b border-zinc-100 px-4 py-3">
            <span className="text-sm font-semibold tracking-tight text-zinc-900">
              Unassigned rates
            </span>
            <span className="text-xs text-zinc-500">
              Not linked to a cabin - edit each rate to attach it.
            </span>
            <span className="flex-1" />
            <span className="text-xs text-zinc-400 tabular-nums">{unassigned.length}</span>
          </div>
          <div className="divide-y divide-zinc-100">
            {unassigned.map((rate) => (
              <div key={rate.id}>
                <RateRow
                  rate={rate}
                  onEdit={() => openRateForm('unassigned', rate)}
                  onDelete={() => setRateDelete(rate)}
                  disabled={pending}
                />
                {rateFormCabin === 'unassigned' && rateEditing?.id === rate.id && (
                  <div className="border-t border-zinc-100 bg-zinc-50/40 p-4">
                    <CabinRateFormPanel
                      key={rateEditing.id}
                      cabins={cabins}
                      initial={rateEditing}
                      pending={pending}
                      onSubmit={submitRate}
                      onCancel={closeRateForm}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Add-rate for the unassigned bucket when no rate is being edited yet. */}
      {rateFormCabin === 'unassigned' && !rateEditing && (
        <div className="rounded-lg border border-zinc-300 bg-white shadow-sm">
          <CabinRateFormPanel
            cabins={cabins}
            initial={null}
            pending={pending}
            onSubmit={submitRate}
            onCancel={closeRateForm}
          />
        </div>
      )}
      {cabins.length > 0 && rateFormCabin !== 'unassigned' && (
        <div className="flex justify-center">
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => openRateForm('unassigned', null)}
            disabled={pending}
            className="text-xs text-zinc-500 hover:text-zinc-900"
          >
            <Plus className="mr-1 h-3 w-3" />
            Add a rate not linked to a cabin
          </UzButton>
        </div>
      )}

      {/* Cabin dialog */}
      {cabinDialog !== null && (
        <CabinFormDialog
          mode={cabinDialog === 'create' ? 'create' : 'edit'}
          cabin={cabinDialog === 'create' ? undefined : cabinDialog}
          onClose={() => setCabinDialog(null)}
          onSubmit={submitCabin}
          pending={createCabin.isPending || updateCabin.isPending}
        />
      )}

      {/* Delete confirms */}
      <UzConfirmDialog
        open={Boolean(cabinDelete)}
        onOpenChange={(o) => {
          if (!o) setCabinDelete(null);
        }}
        title="Delete this cabin category?"
        description={
          cabinDelete
            ? `"${cabinDelete.name}" will be soft-deleted and hidden from lists. Itinerary components using it stay intact.`
            : ''
        }
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={async () => {
          if (!cabinDelete) return;
          await deleteCabin.mutateAsync(cabinDelete.id);
          setCabinDelete(null);
        }}
      />
      <UzConfirmDialog
        open={Boolean(rateDelete)}
        onOpenChange={(o) => {
          if (!o) setRateDelete(null);
        }}
        title="Delete this rate?"
        description={
          rateDelete
            ? `${fmtDate(rateDelete.validityFrom)} - ${fmtDate(rateDelete.validityTo)} will be soft-deleted. Quotes already issued are unaffected.`
            : ''
        }
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={async () => {
          if (!rateDelete) return;
          await deleteRate.mutateAsync(rateDelete.id);
          setRateDelete(null);
        }}
      />
    </div>
  );
};
